import React, { FC, useState, useEffect } from 'react'
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Tooltip
} from 'recharts'
import Select from 'react-select'

interface Props {
    type: string
    heading: string
    agentsStatsData: any
    performancePieChartList: any
    botNames?: any
    filterEnable?: any
}

function getRandomColor() {
    const letters = '0123456789ABCDEF'
    let color = '#'
    for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)]
    }
    return color
}

const RADIAN = Math.PI / 180

const PerformancePieChart: FC<Props> = props => {
    const [selectedPerformance, setSelectedPerformance] = useState('resolvedRequests')
    const [performance, setPerformance]: any = useState([])
    const [botList, setbotList]: any = useState([{ label: 'All bots', value: 'all' }])
    const [selectedOption, setSelectedOption] = useState(botList[0])
    const [selectedBotID, setSelectedBotID] = useState('')
    const [botSelected, setbotSelected] = useState(false)

    const renderLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name }) => {
        const radius = innerRadius + (outerRadius - innerRadius) + 25
        const x = cx + radius * Math.cos(-midAngle * RADIAN)
        const y = cy + radius * Math.sin(-midAngle * RADIAN)

        const value = Math.round(percent * 100)

        return (
            <text x={x} y={y} fill="black" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
                {value ? `${name}(${value})%` : ''}
            </text>
        )
    }

    useEffect(() => {
        // Bot performance Matrics
        const processData = () => {
            if (props.type === 'botPerformance') {
                const botPerformance = {}
                const promises = props.agentsStatsData?.filter(botData => botData.botId).map((item) => {
                    const key = `${item.botId}`
                    const botName = props.botNames[key] ? props.botNames[key] : 'N/A'

                    if(props.botNames[key]) {
                        if (!botPerformance[key]) {
                            botPerformance[key] = {
                                name: botName,
                                value: 0
                            }
                        }
                        botPerformance[key]['value'] += item[selectedPerformance]
                    }
                })
                const botPerformanceValues = Object.values(botPerformance)
                setPerformance(botPerformanceValues)
            } else if (props.type === 'agentPerformance') {
                let agentPerformance: any = {}
                if (props.filterEnable && botSelected) {
                    const selectedBotData = props.agentsStatsData?.filter(botData => botData.botId === selectedBotID)
                    agentPerformance = selectedBotData?.reduce((accumulator, item) => {
                        const key = `${item.agentId}_${item.email}`
                        if (!accumulator[key]) {
                            accumulator[key] = {
                                name: item.email,
                                value: 0
                            }
                        }
                        accumulator[key]['value'] += item[selectedPerformance]
                        return accumulator
                    }, {})
                } else {
                    agentPerformance = props.agentsStatsData?.reduce((accumulator, item) => {
                        const key = `${item.agentId}_${item.email}`
                        if (!accumulator[key]) {
                            accumulator[key] = {
                                name: item.email,
                                value: 0
                            }
                        }
                        accumulator[key]['value'] += item[selectedPerformance]
                        return accumulator
                    }, {})
                }

                const agentPerformanceValues = Object.values(agentPerformance)
                setPerformance(agentPerformanceValues)
            }
        }
        void processData()
    }, [selectedPerformance, props.botNames, selectedBotID])

    useEffect(() => {
        if (Object.keys(props.botNames).length && props.filterEnable) {
            let botLists: any = [{ label: 'All Bots', value: 'all' }]
            const uniqueBotIds = new Set()
            props.agentsStatsData?.map(botId => {
                if (!uniqueBotIds.has(botId.botId) && props.botNames[botId.botId]) {
                    const botName = props.botNames[botId.botId]
                    botLists.push({ label: botName, value: botId.botId })
                    uniqueBotIds.add(botId.botId)
                }
            })
            setbotList(botLists)
        }
    }, [props.botNames])

    function handlePerformanceChange(event) {
        setSelectedPerformance(event.target.value)
    }

    function handleSelectedBotID(option) {
        if (option.value === 'all') {
            setSelectedBotID('')
            setbotSelected(false)
        } else {
            const id = option.value
            setSelectedBotID(id)
            setbotSelected(true)
        }
    }

    function handleOptionSelect(option) {
        handleSelectedBotID(option)
        setSelectedOption(option)
    }

    const dataWithColor = performance.map(item => ({
        ...item,
        color: getRandomColor()
    }))

    const allZero = performance.every(entry => entry.value === 0)

    return <div>
        <h2>{props.heading}</h2>
        {props.filterEnable ? <div style={{ display: 'flex', justifyContent: 'flex-end', paddingBottom: '4px' }}>
                <div style={{ width: '200px' }}>
                    <Select
                        classNamePrefix={'performance-graphs'}
                        id="bots"
                        default={botList[0]}
                        maxMenuHeight={200}
                        style={{ zIndex: 1000 }}
                        options={botList}
                        value={selectedOption}
                        onChange={handleOptionSelect}
                        styles={{
                            container: (styles) => ({
                                ...styles,
                                width: '100%',
                                fontSize: '12px',
                            }),
                            control: (styles) => ({
                                ...styles,
                                minHeight: '25px',
                                height: '25px',
                            }),
                            dropdownIndicator: (styles) => ({
                                ...styles,
                                padding: '0px 4px'
                            })
                        }}
                    />
                </div>
            </div> : <div style={{paddingBottom: '29px'}}></div>}
        <ResponsiveContainer height={300}>
            {allZero ? (<div style={{ textAlign: 'center', paddingTop: '180px' }}>No data available</div>) :
                <PieChart width={500} height={250}>
                    <Pie
                        data={performance}
                        cx={120}
                        cy={200}
                        innerRadius={60}
                        outerRadius={80}
                        label={renderLabel}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                    >
                        {dataWithColor.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                    </Pie>
                    <Tooltip />
                </PieChart>
            }
        </ResponsiveContainer>
    </div>
}

export default PerformancePieChart
